import type { QuestionSummary } from '@compass/client/types/analytics';
import React from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils';

interface QuestionGuideProps {
  summary?: QuestionSummary;
  loading?: boolean;
}

interface ScoringChip {
  label: string;
  cls: string;
}

// The Partial chip carries no point figure on purpose. A finding's raw_score is
// any normalized value in (0, 1) and the points awarded are `weight × raw_score`,
// so there is no single "partial" figure to quote — the Yes/No chips give the
// bounds and Partial sits between them.
const buildScoringChips = (weight: number, allowsPartial: boolean): ScoringChip[] => {
  const chips: ScoringChip[] = [
    { label: `Yes · ${weight} pts`, cls: 'bg-positive text-positive-foreground' },
  ];
  if (allowsPartial) {
    chips.push({ label: 'Partial', cls: 'bg-attention text-attention-foreground' });
  }
  chips.push({ label: 'No · 0 pts', cls: 'bg-critical text-critical-foreground' });
  return chips;
};

export const QuestionGuide: React.FC<QuestionGuideProps> = ({ summary, loading }) => {
  if (loading && !summary) {
    return (
      <div className="rounded-lg border bg-muted/40 px-4 py-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-2 h-4 w-64" />
      </div>
    );
  }
  if (!summary) return null;

  const title = summary.metric_label || summary.metric_key || 'Question';
  const description = summary.description ?? '';
  const prompt = summary.ai_prompt ?? '';
  const scoringChips = buildScoringChips(summary.weight ?? 0, summary.allows_partial ?? false);

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="guide"
      className="rounded-lg border bg-muted/40"
    >
      <AccordionItem value="guide" className="border-b-0">
        <AccordionTrigger className="px-4 py-3 hover:no-underline">
          <div className="flex flex-col gap-1 text-left">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Question Guide
            </span>
            <span className="text-sm font-semibold text-foreground">{title}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          {description ? (
            <p className="text-sm leading-relaxed text-foreground">{description}</p>
          ) : null}

          <div className="mt-3">
            <p className="text-xs font-medium text-muted-foreground">Scoring</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {scoringChips.map(chip => (
                <Badge
                  key={chip.label}
                  variant="outline"
                  className={cn('border-transparent font-normal', chip.cls)}
                >
                  {chip.label}
                </Badge>
              ))}
            </div>
          </div>

          {prompt ? (
            <Accordion type="single" collapsible className="mt-2">
              <AccordionItem value="prompt" className="border-b-0">
                <AccordionTrigger className="py-1 text-xs font-medium text-muted-foreground hover:no-underline">
                  AI scoring prompt
                </AccordionTrigger>
                <AccordionContent>
                  <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap rounded-md border bg-background p-3 text-xs leading-relaxed text-muted-foreground">
                    {prompt}
                  </pre>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : null}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
